package fr.frux.core.designsystem.zoom

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min

const val ZOOM_MIN_SCALE = 1f
const val ZOOM_MAX_SCALE = 4f
const val ZOOM_DOUBLE_TAP_SCALE = 2.5f

// En dessous, une image est considérée à 1x : un pincement relâché à 1,004 ne doit pas bloquer
// le pager pour un zoom invisible.
private const val SCALE_EPSILON = 0.01f

// Environ 1,28x par cran de molette : 4x en cinq ou six crans. Borné par événement, au cas où
// une plateforme livrerait un delta bien plus grand que prévu.
private const val WHEEL_ZOOM_RATE = 0.25f
private const val WHEEL_STEP_MIN = 0.5f
private const val WHEEL_STEP_MAX = 2f

/**
 * Transformation appliquée à l'image : [offset] est la translation en pixels, autour du centre du
 * cadre (origine par défaut de `graphicsLayer`).
 */
data class ZoomTransform(
    val scale: Float,
    val offset: Offset,
) {
    val isZoomed: Boolean get() = scale > ZOOM_MIN_SCALE + SCALE_EPSILON

    companion object {
        val Identity = ZoomTransform(scale = ZOOM_MIN_SCALE, offset = Offset.Zero)
    }
}

/**
 * Taille affichée d'une image de taille [intrinsic] posée en `ContentScale.Fit` dans [container].
 * Taille inconnue (image pas encore chargée) : on borne sur le cadre entier.
 */
fun fittedContentSize(
    intrinsic: Size,
    container: Size,
): Size {
    // Un painter sans taille propre rend Unspecified, voire des NaN : !(w > 0) les écarte aussi.
    if (intrinsic == Size.Unspecified || !(intrinsic.width > 0f) || !(intrinsic.height > 0f)) return container
    val factor = min(container.width / intrinsic.width, container.height / intrinsic.height)
    return Size(intrinsic.width * factor, intrinsic.height * factor)
}

/**
 * Borne [offset] pour que l'image zoomée couvre toujours le cadre sur chaque axe où elle le
 * dépasse, et reste centrée sur l'axe où elle tient entière.
 */
fun clampOffset(
    offset: Offset,
    scale: Float,
    container: Size,
    content: Size,
): Offset {
    val maxX = max(0f, (content.width * scale - container.width) / 2f)
    val maxY = max(0f, (content.height * scale - container.height) / 2f)
    // + 0f ramène -0f à 0f : Offset compare ses bits, et un -0f ne serait plus égal à Offset.Zero.
    return Offset(offset.x.coerceIn(-maxX, maxX) + 0f, offset.y.coerceIn(-maxY, maxY) + 0f)
}

/**
 * Applique un facteur de zoom autour de [pivot] (relatif au centre du cadre) puis un [pan] :
 * le point de l'image sous le pivot reste sous le pivot, à l'échelle bornée près.
 */
fun zoomAround(
    current: ZoomTransform,
    zoomFactor: Float,
    pivot: Offset,
    pan: Offset,
    container: Size,
    content: Size,
): ZoomTransform {
    val scale = (current.scale * zoomFactor).coerceIn(ZOOM_MIN_SCALE, ZOOM_MAX_SCALE)
    val ratio = scale / current.scale
    val offset = pivot - (pivot - current.offset) * ratio + pan
    return ZoomTransform(scale, clampOffset(offset, scale, container, content))
}

/** Double tap : retour à 1x si l'image est zoomée, sinon palier de 2,5x centré sur le point touché. */
fun doubleTapTarget(
    current: ZoomTransform,
    tap: Offset,
    container: Size,
    content: Size,
): ZoomTransform =
    if (current.isZoomed) {
        ZoomTransform.Identity
    } else {
        zoomAround(current, ZOOM_DOUBLE_TAP_SCALE / current.scale, tap, Offset.Zero, container, content)
    }

/**
 * Facteur de zoom d'un événement molette : vers le haut (delta négatif) on agrandit. [deltaPerNotch]
 * ramène le delta de la plateforme à des crans.
 */
fun wheelZoomFactor(
    scrollDeltaY: Float,
    deltaPerNotch: Float,
): Float = exp(-scrollDeltaY / deltaPerNotch * WHEEL_ZOOM_RATE).coerceIn(WHEEL_STEP_MIN, WHEEL_STEP_MAX)

/** Interpolation linéaire entre deux transformations, pour animer un double tap. */
fun lerp(
    start: ZoomTransform,
    stop: ZoomTransform,
    fraction: Float,
): ZoomTransform =
    ZoomTransform(
        scale = start.scale + (stop.scale - start.scale) * fraction,
        offset = start.offset + (stop.offset - start.offset) * fraction,
    )
